import React, { FC } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  clientActionUpdateCollaterals,
  Collateral,
  CollateralDetail,
  CollateralStatus,
  CrudActions,
  CrudEventEnum,
  EntityTypeEnum,
  WorkflowActions
} from 'api'
import { Locale, Translation } from 'locale'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { ActionsButtonProps } from 'view/components/datagrid'
import { CollateralDialogEdit, CollateralDialogView } from '../CollateralDialog'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { isCollateralActiveSelector } from 'selectors/collateralSelectors'

export type CollateralActionsButtonProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps, ActionsButtonProps<CollateralDetail> {}

interface StateProps {
  isCollateralActive: (collateral: Collateral) => Boolean
}

interface DispatchProps {
  onShowCollateralEntityDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const mapStateToProps = createStructuredSelector({
  isCollateralActive: isCollateralActiveSelector
})

const mapDispatchToProps = {
  onShowCollateralEntityDialog: WorkflowActions.createEditWorkflow,
  onShowDialog: NotificationActions.showDialog
}

export const CollateralActionsButtonComponent: FC<CollateralActionsButtonProps> = props => {
  const { items, isCollateralActive, showModal, onShowDialog, onShowCollateralEntityDialog } = props
  const getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = props
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  const handleEditItemDialog = (entityId: string) => {
    const correlatedResponseCallback = (workflowId: string) => {
      showModal(<CollateralDialogEdit workflowId={workflowId} collateralEntityId={entityId} collateralPriceEntityId={entityId} />)
    }
    onShowCollateralEntityDialog(EntityTypeEnum.COLLATERAL, entityId, correlatedResponseCallback)
  }

  const handleViewItemDialog = (entityId: string) =>
    showModal(<CollateralDialogView collateralEntityId={entityId} collateralPriceEntityId={entityId} />)

  const handleSuspendItems = (newSuspendItems: CollateralDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

    const messageText =
      newSuspendItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_COLLATERAL, { name: newSuspendItems[0].collateral.name })
        : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_COLLATERAL, { count: newSuspendItems.length })

    const suspendActions = newSuspendItems.map(newSuspendItem =>
      CrudActions.collateralUpsert({
        ...newSuspendItem.collateral,
        status: CollateralStatus.DISABLED
      })
    )

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  const handleActivateItems = (newActivateItems: CollateralDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

    const messageText =
      newActivateItems.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_COLLATERAL, { name: newActivateItems[0].collateral.name })
        : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_COLLATERAL, { count: newActivateItems.length })

    const activateActions = newActivateItems.map(activateItem =>
      CrudActions.collateralUpsert({
        ...activateItem.collateral,
        status: CollateralStatus.ENABLED
      })
    )

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: activateActions
      }
    })
  }

  const handleUpdateItemFromRefinitiv = (collateralId: string) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_UPDATE_FROM_REFINITIV)
    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_UPDATE_FROM_REFINITIV)
    const updateCollateral = clientActionUpdateCollaterals({ collateralIds: [collateralId], includeFieldNames: [], excludeFieldNames: [] })

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [updateCollateral]
      }
    })
  }

  const canEditDetails = (item: CollateralDetail) => {
    return item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  const canSuspend = (item: CollateralDetail) => {
    return item.collateral.status !== CollateralStatus.DISABLED && item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  const canActivate = (item: CollateralDetail) => {
    if (
      item.collateral.status !== CollateralStatus.ENABLED &&
      item.availableEvents.includes(CrudEventEnum.UPSERT) &&
      isCollateralActive(item.collateral)
    ) {
      return true
    }

    return false
  }

  const canUpdateFromRifinitiv = (item: CollateralDetail) => {
    return item.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  const editItems = items.filter(item => canEditDetails(item))
  const viewItems = items.filter(item => !canEditDetails(item))
  const suspendItems = items.filter(item => canSuspend(item))
  const activateItems = items.filter(item => canActivate(item))
  const updateFromRifinitivItems = items.filter(item => canUpdateFromRifinitiv(item))

  const actions: ActionItem[] = [
    {
      label: getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
      onClick: () => handleViewItemDialog(viewItems[0].collateral.id),
      enabled: viewItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
      onClick: () => handleEditItemDialog(editItems[0].collateral.id),
      enabled: editItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_SUSPEND_TRADING, suspendItems.length),
      onClick: () => handleSuspendItems(suspendItems),
      enabled: suspendItems.length > 0
    },
    {
      label: getLabel(Translation.TC_ACTION_ACTIVATE_TRADING, activateItems.length),
      onClick: () => handleActivateItems(activateItems),
      enabled: activateItems.length > 0
    },
    {
      label: getLabel(Translation.TC_ACTION_UPDATE_FROM_RIFINITIV, 1),
      onClick: () => handleUpdateItemFromRefinitiv(updateFromRifinitivItems[0].collateral.id),
      enabled: updateFromRifinitivItems.length === 1
    }
  ]

  return <SplitButtonDropdown dataTestId={'CollateralActionsButton'} actions={actions} maxButtons={1} />
}

export const CollateralActionsButton = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(CollateralActionsButtonComponent)
)
